import { defineElement, FC, onDidLoad, useElement, useListen } from '@atomify/hooks';
import { rafThrottle } from '@bpd-library/utilities';

const VIEW_TOOLTIP = 'tooltip--visible';
const TOP_FALLBACK = 'tooltip__item--top-fallback';
const MARGIN = 16;

const repositionTooltip = (item: HTMLElement) => {
    // Clear previous dynamic adjustments before re-measuring
    item.style.removeProperty('left');
    item.style.removeProperty('--caret-offset');
    item.classList.remove(TOP_FALLBACK);

    const rect = item.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;

    const isLeft = item.classList.contains('tooltip__item--left');
    const isRight = item.classList.contains('tooltip__item--right');

    // Side-direction fallback: if the primary edge clips, switch to top
    if (isLeft && rect.left < MARGIN) {
        item.classList.add(TOP_FALLBACK);
    } else if (isRight && rect.right > vw - MARGIN) {
        item.classList.add(TOP_FALLBACK);
    }

    // Re-measure after any fallback class change
    const r = item.getBoundingClientRect();
    let delta = 0;

    if (r.left < MARGIN) {
        delta = MARGIN - r.left;
        item.style.left = `${item.offsetLeft + delta}px`;
    } else if (r.right > vw - MARGIN) {
        delta = -(r.right - (vw - MARGIN));
        item.style.left = `${item.offsetLeft + delta}px`;
    }

    if (delta !== 0) {
        item.style.setProperty('--caret-offset', `${-delta}px`);
    }
};

const BPDTooltip: FC = ({ element }) => {
    const button = useElement<HTMLButtonElement>('[js-hook-tooltip-icon]');
    const tooltipItem = useElement<HTMLElement>('.tooltip__item');

    useListen(button, 'click', () => {
        element.classList.toggle(VIEW_TOOLTIP);
        if (tooltipItem.current) repositionTooltip(tooltipItem.current);
    });

    useListen(button, 'mouseenter', () => {
        if (tooltipItem.current) repositionTooltip(tooltipItem.current);
    });

    useListen(button, 'mouseleave', () => {
        if (tooltipItem.current) repositionTooltip(tooltipItem.current);
    });

    onDidLoad(() => {
        // Pre-position while hidden so the default CSS placement never causes layout overflow
        if (tooltipItem.current) repositionTooltip(tooltipItem.current);

        const handleResize = rafThrottle(() => {
            if (tooltipItem.current) repositionTooltip(tooltipItem.current);
        });

        window.addEventListener('resize', handleResize);
    });
};

defineElement('bpd-tooltip', BPDTooltip);
